import React, { useCallback } from 'react';
import styles from './financialOverview.module.css';
import { Box, Flex } from '@chakra-ui/react';
import { ArrowUpIcon, ArrowDownIcon } from '@spotoninc/nexus-web-assets';
import { toAmountString } from '../tasks.utils';

/**
 *
 * @param {object} props
 * @param {boolean} props.showDownArrow - Whether to show the down arrow or up arrow
 * @param {boolean} props.attentionRequired - Whether to show the attention required label
 * @param {number} props.percentage - The percentage to show
 * @param {string} props.label - The label to show
 * @returns
 */
function FinancialStats({
  showDownArrow,
  attentionRequired = false,
  percentage,
  label,
}) {
  return (
    <Flex direction="row" items="center" gap={1}>
      <Flex
        direction="row"
        alignItems={'center'}
        gap={1}
        className={`${styles.percentage} ${attentionRequired ? styles.attentionRequired : ''}`}
      >
        <Box border={1} borderColor={'black'}>
          {showDownArrow ? (
            <ArrowDownIcon className={styles.icon} />
          ) : (
            <ArrowUpIcon className={styles.icon} />
          )}
        </Box>
        <Box border={1} borderColor={'black'}>
          {percentage}%
        </Box>
      </Flex>
      <Box className={styles.label}>{label}</Box>
    </Flex>
  );
}
/**
 * It will render overview part of the financial information
 *
 * Parent
 *      TaskDrawer
 */
function FinancialOverview({ netIncome, revenue, expenses }) {
  // Helper function to determine if arrow should point down and if attention is required
  const getStatsProps = useCallback(value => {
    if (!value)
      return { showDownArrow: false, attentionRequired: false, percentage: 0 };

    const numValue = parseFloat(value);
    const showDownArrow = numValue < 0;
    const attentionRequired = numValue < 0; // Attention required if decrease > 5%

    return {
      showDownArrow,
      attentionRequired,
      percentage: Math.abs(numValue),
    };
  }, []);

  return (
    <Flex direction={'column'} flex={1}>
      <Box className={`${styles.field} ${styles.fieldBorder}`}>
        <Box className={styles.blockLabel}>Revenue</Box>
        <Flex
          direction={'row'}
          alignItems={'flex-end'}
          gap={2}
          pt={1}
          borderBottom={1}
          borderColor={'gray'}
          wrap={'wrap'}
        >
          <Box className={styles.amount}>{toAmountString(revenue?.amount)}</Box>
          {revenue?.yoy && (
            <Box pb={1}>
              <FinancialStats {...getStatsProps(revenue.yoy)} label="YOY" />
            </Box>
          )}
          {revenue?.mom && (
            <Box pb={1}>
              <FinancialStats {...getStatsProps(revenue.mom)} label="MOM" />
            </Box>
          )}
        </Flex>
      </Box>
      <Box className={`${styles.field} ${styles.fieldBorder}`}>
        <Box className={styles.blockLabel}>Expenses</Box>
        <Flex
          direction={'row'}
          alignItems={'flex-end'}
          gap="3"
          pt={1}
          borderBottom={1}
          borderColor={'gray'}
          wrap={'wrap'}
        >
          <Box className={styles.amount}>
            {toAmountString(expenses?.amount)}
          </Box>
          {expenses?.yoy && (
            <Box pb={1}>
              <FinancialStats {...getStatsProps(expenses.yoy)} label="YOY" />
            </Box>
          )}
          {expenses?.mom && (
            <Box pb={1}>
              <FinancialStats {...getStatsProps(expenses.mom)} label="MOM" />
            </Box>
          )}
        </Flex>
      </Box>
      <Box className={styles.field}>
        <Box className={styles.blockLabel}>Net Income</Box>
        <Flex
          direction={'row'}
          alignItems={'flex-end'}
          gap="3"
          pt={1}
          borderBottom={1}
          borderColor={'gray'}
          wrap={'wrap'}
        >
          <Box className={styles.amount}>
            {toAmountString(netIncome?.amount)}
          </Box>
          {netIncome?.percentage && (
            <Box pb={1}>
              <FinancialStats
                showDownArrow={false}
                attentionRequired={false}
                percentage={netIncome?.percentage}
                label="of revenue"
              />
            </Box>
          )}
        </Flex>
      </Box>
    </Flex>
  );
}

export default FinancialOverview;
